<template>
  <!-- Alerta fixo: aparece enquanto o e-mail da conta não estiver confirmado (some sozinho depois da confirmação) -->
  <div v-if="exibir" ref="raiz" class="eca">
    <button type="button" class="eca-card" :class="invalido ? 'eca-invalido' : 'eca-pendente'" :aria-expanded="aberto ? 'true' : 'false'"
      aria-haspopup="true" :title="status.mensagem" @click="aberto = !aberto" @keydown.esc="aberto = false">
      <svg class="eca-icone" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </svg>
      <span class="eca-texto">{{ invalido ? 'E-mail inválido' : 'Confirme seu e-mail' }}</span>
    </button>

    <div v-show="aberto" class="eca-pop" role="region" aria-label="Confirmação do e-mail da conta">
      <div class="eca-pop-titulo">{{ invalido ? 'E-mail da conta inválido' : 'E-mail da conta não confirmado' }}</div>
      <p class="eca-pop-msg">{{ status.mensagem }}</p>
      <div class="eca-pop-acoes">
        <button v-if="status.podeReenviar" type="button" class="btn btn-primary btn-sm" :disabled="enviando" @click="reenviar">
          {{ enviando ? 'Enviando...' : 'Reenviar link de confirmação' }}
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="verificando" @click="verificar(true)">
          {{ verificando ? 'Verificando...' : 'Já confirmei' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { statusEmailConta, reenviarLinkEmailConta } from '@/stores/emailContaStore';
import { showAlert, showToast } from '@/mixins/fcgeral';

const status = ref({ confirmado: true, situacao: 'confirmado', podeReenviar: false, mensagem: '', email: '' });
const aberto = ref(false);
const enviando = ref(false);
const verificando = ref(false);
const raiz = ref(null);
let timer = null;

// Reconfere de tempos em tempos e ao voltar para a aba (o cliente confirma o link em outra aba)
const INTERVALO_MS = 5 * 60 * 1000;

const exibir = computed(() => !status.value.confirmado && !!status.value.mensagem);
const invalido = computed(() => status.value.situacao === 'invalido');

async function verificar(manual = false) {
  verificando.value = manual;
  const json = await statusEmailConta();
  verificando.value = false;
  if (json?.ok && json.data) {
    status.value = json.data;
    if (manual && json.data.confirmado) showToast('success', 'E-mail confirmado. Obrigado!');
    else if (manual) showToast('info', 'Ainda não identificamos a confirmação.');
  }
}

async function reenviar() {
  enviando.value = true;
  const json = await reenviarLinkEmailConta();
  enviando.value = false;
  if (json?.ok) showToast('success', json.msg || 'Link enviado.');
  else showAlert('warning', json?.msg || 'Não foi possível enviar o link agora.');
}

function aoVoltarParaAba() {
  if (document.visibilityState === 'visible') verificar();
}

function foraDoComponente(e) {
  if (raiz.value && !raiz.value.contains(e.target)) aberto.value = false;
}

onMounted(() => {
  verificar();
  timer = setInterval(verificar, INTERVALO_MS);
  document.addEventListener('visibilitychange', aoVoltarParaAba);
  document.addEventListener('pointerdown', foraDoComponente);
});

onBeforeUnmount(() => {
  clearInterval(timer);
  document.removeEventListener('visibilitychange', aoVoltarParaAba);
  document.removeEventListener('pointerdown', foraDoComponente);
});
</script>

<style scoped>
.eca {
  position: relative;
  font-size: 0.8rem;
  line-height: 1.2;
  --eca-aviso: #e2a03f;
  --eca-erro: #e7515a;
}

.eca-card {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid var(--eca-aviso);
  border-radius: 6px;
  background: rgba(226, 160, 63, 0.12);
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.eca-card:focus-visible { outline: 2px solid var(--eca-aviso); outline-offset: 2px; }
.eca-card.eca-pendente .eca-icone { color: var(--eca-aviso); }
.eca-card.eca-invalido { border-color: var(--eca-erro); background: rgba(231, 81, 90, 0.12); }
.eca-card.eca-invalido .eca-icone { color: var(--eca-erro); }

.eca-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1100;
  width: 340px;
  max-width: 92vw;
  padding: 12px 14px;
  background: #fff;
  color: #3b3f5c;
  border: 1px solid #e0e6ed;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  white-space: normal;
}
.eca-pop-titulo { font-weight: 600; margin-bottom: 6px; }
.eca-pop-msg { margin: 0 0 10px; opacity: 0.85; overflow-wrap: anywhere; }
.eca-pop-acoes { display: flex; flex-wrap: wrap; gap: 8px; }

/* tema escuro (mesmas cores do header em dark.scss) */
.dark .eca-card { color: #bfc9d4; }
.dark .eca-pop { background: #0e1726; color: #bfc9d4; border-color: #1b2e4b; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5); }

@media (max-width: 575px) {
  .eca-texto { display: none; }
  .eca-pop { position: fixed; top: 60px; left: 8px; right: 8px; width: auto; }
}
</style>
